import { Arrays }		from "ts-base/arrays";

import { FormSchema }		from "@v3/geko/form/form";
import * as fields			from "@v3/geko/form/fields";

import * as fbModel		from "@geotoura/shared/fbModel";
import * as commonModel	from "@geotoura/shared/commonModel";

export type Model = Readonly<{
	questionnaireId:	fbModel.QuestionnaireId,
	questionnaireName:	string,
	questionnaireData:	QuestionnaireData | null,
	formDirty:			boolean,
	serverResponse:		string,
	displayMode:		DisplayMode,
	pageInfo:			PageInfo,
}>;

export namespace Model {
	export const initial = (questionnaireId:fbModel.QuestionnaireId, questionnaireName:string, pageInfo:PageInfo):Model => ({
		questionnaireId:	questionnaireId,
		questionnaireName:	questionnaireName,
		questionnaireData:	null,
		formDirty:			false,
		serverResponse:		"",
		displayMode:		"integrated",
		pageInfo,
	});
}

export type PageInfo = FrontPageInfo | RoutePageInfo | RegionPageInfo | TerritoryPageInfo;

type FrontPageInfo = Readonly<{
	type: "front",
}>;

type RoutePageInfo = Readonly<{
	type: "route",
	routeId: commonModel.ExampleRouteId,
}>;

type RegionPageInfo = Readonly<{
	type:		"region",
	routeId:	commonModel.ExampleRouteId,
	regionId:	commonModel.RegionId,
}>;

export type TerritoryPageInfo = Readonly<{
	type:			"territory",
	territoryId:	commonModel.TerritoryId,
}>;

export type DisplayMode = "fullscreen"|"integrated";

export type QuestionnaireData = Readonly<{
	// TODO fbtype can we ensure questionnaire.screens and answers are type-aligned?
	questionnaire:	fbModel.Questionnaire,
	answers:		ReadonlyArray<fbModel.Answer>,
	contactData:	ContactModel,
	currentScreen:	number,
}>;

export const ContactSchema	=
	FormSchema.of({
		returnTitle:		fields.FreeSelect<commonModel.Title>(commonModel.Title.brand("")),
		returnGender:		fields.FreeSelect<commonModel.Gender|null>(null),
		returnFirstName:	fields.FreeText(commonModel.FirstName)(commonModel.FirstName.brand("")),
		returnLastName:		fields.FreeText(commonModel.LastName)(commonModel.LastName.brand("")),
		returnMail:			fields.RequiredText(commonModel.EmailAddressLtd)(null),
		returnTextarea:		fields.FreeText(commonModel.ParameterValue)(commonModel.ParameterValue.brand("")),
		returnCheckbox:		fields.RequiredCheckbox(false),
	});

export type ContactModel	= FormSchema.ModelOf<typeof ContactSchema>;
export type ContactData		= FormSchema.ExtractOf<typeof ContactSchema>;

//-----------------------------------------------------------------------------
//## setup

export const initQuestionnaire	= (questionnaire:fbModel.Questionnaire):QuestionnaireData	=> ({
	questionnaire:	questionnaire,
	answers:		initAnswers(questionnaire.screens),
	contactData:	ContactSchema.initial(),
	currentScreen:	0,
});

// NOTE the last screen is just a "thanks for contacting us", no answer is expected there
const initAnswers	= (screens:ReadonlyArray<fbModel.Screen>):ReadonlyArray<fbModel.Answer> =>
	Arrays.init(screens).map(initAnswer);

const initAnswer = (screen:fbModel.Screen):fbModel.Answer => {
	switch (screen.stId) {
		case "RADIOSTART":
			const contentSTRadiostart	= screen.content;
			return {
				screenId:				screen.id,
				questionnaireScreenId:	screen.questionnaireScreenId,
				stId:					"RADIOSTART",
				content: {
					radioItemId:	null,
					keyRadio:		contentSTRadiostart.radioGroup.keyRadio,
					valueRadio:		null,
					valueDefault:	null,
					mailRadioKey:	contentSTRadiostart.radioGroup.mailKey,
					mailRadioValue:	"",
				},
				show:					true,
			};
		case "SELECTLIST":
			const contentSTSelectList	= screen.content;
			return {
				screenId:				screen.id,
				questionnaireScreenId:	screen.questionnaireScreenId,
				stId:					"SELECTLIST",
				content: {
					selected:			[],
					selectedDefault:	[],
					keyList:			contentSTSelectList.keyList,
					mailListKey:		contentSTSelectList.mailListKey,
				},
				show:					true,
			};
		case "DATE":
			const contentSTDate	= screen.content;
			return {
				screenId:				screen.id,
				questionnaireScreenId:	screen.questionnaireScreenId,
				stId:					"DATE",
				content: {
					counter1: {
						keyCounter:		contentSTDate.counter1.keyCounter,
						counterValue:	contentSTDate.counter1.counterDefault,
						counterDefault:	contentSTDate.counter1.counterDefault,
						mailKey:		contentSTDate.counter1.mailKey,
					},
					counter2: {
						keyCounter:		contentSTDate.counter2.keyCounter,
						counterValue:	contentSTDate.counter2.counterDefault,
						counterDefault:	contentSTDate.counter2.counterDefault,
						mailKey:		contentSTDate.counter2.mailKey,
					},
					date:				"",
					dateDefault:		"",
					keyDate:			contentSTDate.keyDate,
					mailDateKey:		contentSTDate.mailDateKey,
				},
				show:					true,
			};
		case "FLIGHT":
			const contentSTFlight	= screen.content;
			return {
				screenId:				screen.id,
				questionnaireScreenId:	screen.questionnaireScreenId,
				stId:					"FLIGHT",
				content: {
					radioItemId:		null,
					valueDefault:		null,
					keyRadio:			contentSTFlight.radioGroup.keyRadio,
					keyCountry:			contentSTFlight.keyCountry,
					keyPostcode:		contentSTFlight.keyPostcode,
					valueRadio:			null,
					countryValue:		"",
					valuePostcode:		commonModel.PostCode.brand(""),
					mailModalTitle:		contentSTFlight.mailModalTitle,
					mailCountryTitle:	contentSTFlight.mailCountryTitle,
					mailPostcodeTitle:	contentSTFlight.mailPostcodeTitle,
					mailRadioKey:		contentSTFlight.radioGroup.mailKey,
					mailRadioValue:		"",
				},
				show:					true,
			};
		case "HOTEL":
			const contentSTHotel	= screen.content;
			return {
				screenId:				screen.id,
				questionnaireScreenId:	screen.questionnaireScreenId,
				stId:					"HOTEL",
				content: {
					counter: {
						keyCounter:		contentSTHotel.counter.keyCounter,
						counterValue:	contentSTHotel.counter.counterDefault,
						counterDefault:	contentSTHotel.counter.counterDefault,
						mailKey:		contentSTHotel.counter.mailKey,
					},
					radioItemId:		null,
					keyRadio:			contentSTHotel.radioGroup.keyRadio,
					valueRadio:			null,
					valueDefault:		null,
					mailRadioKey:		contentSTHotel.radioGroup.mailKey,
					mailRadioValue:		"",
				},
				show:					true,
			};
		case "COUNTERLIST":
			const contentSTCounterList	= screen.content;
			return {
				screenId:				screen.id,
				questionnaireScreenId:	screen.questionnaireScreenId,
				stId:					"COUNTERLIST",
				content: {
					counterValues: contentSTCounterList.list.map(it => ({
						keyCounter:		it.keyCounter,
						counterValue:	it.counterDefault,
						counterDefault:	it.counterDefault,
						mailKey:		it.mailKey,
					})),
				},
				show:					true,
			};
		case "SLIDERLIST":
			const contentSTSliderList	= screen.content;
			return {
				screenId:				screen.id,
				questionnaireScreenId:	screen.questionnaireScreenId,
				stId:					"SLIDERLIST",
				content: {
					sliderValues: contentSTSliderList.sliders.map(it => ( {
						keySlider:		it.keySlider,
						sliderValue:	fbModel.SliderValues.neutral,
						sliderDefault:	fbModel.SliderValues.neutral,
						mailValue:		"",
						mailKey:		it.mailKey,
					})),
				},
				show:					true,
			};
		case "BUDGET":
			const contentSTBudget	= screen.content;
			return {
				screenId:				screen.id,
				questionnaireScreenId:	screen.questionnaireScreenId,
				stId:					"BUDGET",
				content: {
					budget:				fbModel.DayAverages.budgetEuroToPercent(contentSTBudget.dayDefault, contentSTBudget.dayAverages),
					budgetDefault:		fbModel.DayAverages.budgetEuroToPercent(contentSTBudget.dayDefault, contentSTBudget.dayAverages),
					dayAverages:		contentSTBudget.dayAverages,
					mailBudgetKey:		contentSTBudget.mailBudgetKey,
				},
				show:					true,
			};
		case "CONTACT":
			const contentSTContact	= screen.content;
			return {
				screenId:				screen.id,
				questionnaireScreenId:	screen.questionnaireScreenId,
				stId:					"CONTACT",
				content: {
					keyComment:			contentSTContact.keyComment,
					keyMail:			contentSTContact.keyMail,
					keyTitle:			"title",
					keyGender:			"gender",
					keyFirstName:		"first_name",
					keyLastName:		"last_name",
					keyCheckbox:		contentSTContact.keyCheckbox,
					returnTextarea:		commonModel.ParameterValue.brand(""),
					returnDefault:		"",
					returnMail:			commonModel.EmailAddressLtd.brand(""),
					returnTitle:		commonModel.Title.brand(""),
					returnGender:		null,
					returnFirstName:	commonModel.FirstName.brand(""),
					returnLastName:		commonModel.LastName.brand(""),
					returnCheckbox:		false,
					mailContactKey:		contentSTContact.mailContactKey,
				},
				show:					true,
			};
		case "TEXT":
			// const contentSTText	= screen.content;
			return {
				screenId:				screen.id,
				questionnaireScreenId:	screen.questionnaireScreenId,
				stId:					"TEXT",
				// this is AText: a text screen does not need answer data
				content:				{},
				show:					true,
			};
		case "DECISION":
			const contentSTDecision	= screen.content;
			return {
				screenId:				screen.id,
				questionnaireScreenId:	screen.questionnaireScreenId,
				stId:					"DECISION",
				content: {
					valueRadio:			null,
					valueDefault:		null,
					keyRadio:			contentSTDecision.radioGroup.keyRadio,
					radioItemId:		null,
					mailRadioKey:		contentSTDecision.radioGroup.mailKey,
					mailRadioValue:		"",
				},
				show:					true,
			};
	}
};

//-----------------------------------------------------------------------------
//## screen mode and progress

/*
Assumptions:
1) There is a variable number of screens
2) the pre-last screen contains the last form elements – they will be sent to the server when clicking next and the form is valid
3) the last screen contains just a thank you note - it will lead back to the main page
*/
export type ScreenMode	= "first"|"other"|"send"|"thanks";

export const currentScreenMode	= (data:QuestionnaireData):ScreenMode	=> {
	const screens	= data.questionnaire.screens.length;
	const current	= data.currentScreen;
	return	current === 0			? "first"
		:	current === screens - 2	? "send"
		:	current === screens - 1 ? "thanks"
		:	"other";
};

/** returns a number between 0 and 1 (inclusive) */
export const progress	= (data:QuestionnaireData):number =>
	// the "thanks"-screen has index screens.length-1. this counts as 100% done.
	data.currentScreen / (data.questionnaire.screens.length-1);

/*
// NOTE we could use these instead, but that lead to jumpy progress
const doneSreens	= data.answers.filter((answer, idx) => answer.show && idx < data.currentScreen);
const shownScreens	= data.answers.filter(it => it.show);
*/
